import { useState, useCallback, useMemo } from 'react';

export interface POSCartItem {
  key: string;
  productId: number;
  variantId?: number;
  sizeName?: string;
  productName: string;
  productImage?: string;
  sku: string;
  unitPrice: number;
  costPrice: number;
  quantity: number;
  discount: number;
  maxStock: number;
  color?: string;
}

export function usePOSCart() {
  const [items, setItems] = useState<POSCartItem[]>([]);
  const [orderDiscount, setOrderDiscount] = useState<number>(0);
  const [customerName, setCustomerName] = useState<string>('');
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const buildKey = (productId: number, variantId?: number, sizeName?: string) =>
    `${productId}:${variantId || ''}:${sizeName || ''}`;

  const addItem = useCallback((item: Omit<POSCartItem, 'key' | 'quantity' | 'discount'>, quantity: number = 1) => {
    const key = buildKey(item.productId, item.variantId, item.sizeName);

    setItems(prev => {
      const existing = prev.find(i => i.key === key);
      if (existing) {
        return prev.map(i =>
          i.key === key
            ? { ...i, quantity: Math.min(i.quantity + quantity, i.maxStock) }
            : i
        );
      }
      return [...prev, { ...item, key, quantity: Math.min(quantity, item.maxStock), discount: 0 }];
    });
  }, []);

  const updateQuantity = useCallback((key: string, quantity: number) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(i => i.key !== key));
      return;
    }
    setItems(prev =>
      prev.map(i => (i.key === key ? { ...i, quantity: Math.min(quantity, i.maxStock) } : i))
    );
  }, []);

  const updateItemDiscount = useCallback((key: string, discount: number) => {
    setItems(prev =>
      prev.map(i => (i.key === key ? { ...i, discount: Math.max(0, discount) } : i))
    );
  }, []);

  const removeItem = useCallback((key: string) => {
    setItems(prev => prev.filter(i => i.key !== key));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setOrderDiscount(0);
    setCustomerName('');
    setCustomerPhone('');
    setNotes('');
  }, []);

  const loadItems = useCallback((loadedItems: POSCartItem[], info?: { customerName?: string; customerPhone?: string; notes?: string; orderDiscount?: number }) => {
    setItems(loadedItems);
    if (info?.customerName) setCustomerName(info.customerName);
    if (info?.customerPhone) setCustomerPhone(info.customerPhone);
    if (info?.notes) setNotes(info.notes);
    if (info?.orderDiscount) setOrderDiscount(info.orderDiscount);
  }, []);

  const subtotal = useMemo(() => items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0), [items]);
  const itemDiscountTotal = useMemo(() => items.reduce((sum, i) => sum + i.discount, 0), [items]);
  const totalAmount = useMemo(() => Math.max(0, subtotal - itemDiscountTotal - orderDiscount), [subtotal, itemDiscountTotal, orderDiscount]);
  const totalItems = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);
  const totalCost = useMemo(() => items.reduce((sum, i) => sum + i.costPrice * i.quantity, 0), [items]);

  return {
    items,
    orderDiscount,
    customerName,
    customerPhone,
    notes,
    subtotal,
    itemDiscountTotal,
    totalAmount,
    totalItems,
    totalCost,
    addItem,
    updateQuantity,
    updateItemDiscount,
    removeItem,
    clearCart,
    loadItems,
    setOrderDiscount,
    setCustomerName,
    setCustomerPhone,
    setNotes,
  };
}
